import { useReactTable } from '@tanstack/react-table';
import {
  ColumnFiltersState, createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState,SortingState
} from '@tanstack/table-core';
import clsx from 'clsx';
import React, {
  Suspense, useCallback, useEffect, useMemo, useState
} from 'react';
import {
  FiEdit2, FiInfo,FiMessageSquare, FiMoreVertical, FiPlus, FiTrash2
} from 'react-icons/fi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import { DeleteModal } from '@/components/deleteModal/deleteModal';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { Tooltip } from '@/components/tooltip/tooltip';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useDeleteModel, useModelsQuery } from '@/hooks/queries/useModelsQuery';
import { useProvidersQuery } from '@/hooks/queries/useProvidersQuery';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { Model } from '@/types/model';

import { ModelModal } from './modelModal';
import pageStyles from './page.module.scss';

const columnHelper = createColumnHelper<Model>();

function ModelsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [ useLocalZone ] = useLocalZonePreference();
  const [ sorting, setSorting ] = useState<SortingState>( [] );
  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>( [] );
  const [ pagination, setPagination ] = useState<PaginationState>( {
    pageIndex: 0,
    pageSize: 30
  } );
  const [ isModalOpen, setIsModalOpen ] = useState( false );
  const [ modalMode, setModalMode ] = useState<'add' | 'edit'>( 'add' );
  const [ editingModelId, setEditingModelId ] = useState<string | undefined>( undefined );
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
  const [ deletingModel, setDeletingModel ] = useState<{ id: string; name: string } | null>( null );

  // React Query hooks
  const { data: models, isPending: isLoading, isError, error } = useModelsQuery( { enabled: true } );
  const { data: providers = [], isLoading: isLoadingProviders } = useProvidersQuery( { enabled: true } );
  const deleteModelMutation = useDeleteModel();

  const handleAddModel = useCallback( () => {
    setModalMode( 'add' );
    setEditingModelId( undefined );
    setIsModalOpen( true );
  }, [] );

  const handleEditModel = useCallback( ( modelUuid: string ) => {
    setModalMode( 'edit' );
    setEditingModelId( modelUuid );
    setIsModalOpen( true );
  }, [] );

  const handleCloseModal = useCallback( () => {
    setIsModalOpen( false );
    setEditingModelId( undefined );
  }, [] );

  const handleModalSuccess = useCallback( () => {
    // Refresh the models list after successful operation
    toast.success( modalMode === 'add' ? 'Model added successfully' : 'Model updated successfully' );
    // The useModelsQuery will automatically refetch
  }, [ modalMode ] );

  const handleDeleteModel = useCallback( ( modelUuid: string, modelName: string ) => {
    setDeletingModel( { id: modelUuid, name: modelName } );
    setIsDeleteModalOpen( true );
  }, [] );

  const handleCloseDeleteModal = useCallback( () => {
    setIsDeleteModalOpen( false );
    setDeletingModel( null );
  }, [] );

  const handleConfirmDelete = useCallback( async() => {
    if ( !deletingModel ) return;

    await deleteModelMutation.mutateAsync( deletingModel.id );
    toast.success( 'Model deleted successfully' );
  }, [ deletingModel, deleteModelMutation ] );

  const handleRowClick = useCallback( ( model: Model ) => {
    const modelUuid = model.uuid || model.name;
    const params = new URLSearchParams( { id: modelUuid } );
    navigate( `/models/details/?${ params.toString() }` );
  }, [ navigate ] );

  const columns = useMemo( () => [
    columnHelper.accessor( 'publicName', {
      header: () => (
        <span style={ { display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' } }>
          Model Name
          <Tooltip content="The name used within this platform. Shown to users when selecting or configuring models.">
            <Icon
              icon={ FiInfo }
              style={ { fontSize: '12px' } }
            />
          </Tooltip>
        </span>
      ),
      cell: info => {
        const publicName = info.getValue() || info.row.original.name;
        return (
          <span title={ publicName } style={ { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }>
            { publicName }
          </span>
        );
      },
      enableSorting: true,
      sortingFn: 'alphanumeric',
      sortDescFirst: false,
      filterFn: ( row, _columnId, filterValue ) => {
        if ( !filterValue ) return true;
        const publicName = ( row.original.publicName || row.original.name || '' ).toLowerCase();
        const internalName = ( row.original.internalName || row.original.name || '' ).toLowerCase();
        return publicName.includes( filterValue ) || internalName.includes( filterValue );
      },
      size: 250
    } ),
    columnHelper.accessor( 'internalName', {
      header: () => (
        <span style={ { display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' } }>
          Provider Model
          <Tooltip content="The provider's model identifier. This value is sent to the provider's API at runtime.">
            <Icon
              icon={ FiInfo }
              style={ { fontSize: '12px' } }
            />
          </Tooltip>
        </span>
      ),
      cell: info => {
        const internalName = info.getValue() || info.row.original.name;
        return (
          <span title={ internalName } style={ { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }>
            { internalName }
          </span>
        );
      },
      enableSorting: true,
      sortingFn: 'alphanumeric',
      sortDescFirst: false,
      size: 250
    } ),
    columnHelper.accessor( 'provider', {
      header: 'Provider',
      cell: info => (
        <div style={ { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }>
          {info.getValue() || info.row.original.source || 'N/A'}
        </div>
      ),
      enableSorting: true,
      sortingFn: 'alphanumeric',
      size: 150
    } ),
    columnHelper.display( {
      id: 'play',
      header: 'Play',
      enableSorting: false,
      cell: info => {
        const modelName = info.row.original.publicName || info.row.original.name;
        const params = new URLSearchParams( { mn: modelName } );
        return (
          <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center' } }>
            <Link
              to={ `/models/playground/?${ params.toString() }` }
              title="Open in playground"
            >
              <Icon icon={ FiMessageSquare } />
            </Link>
          </div>
        );
      },
      size: 40
    } ),
    columnHelper.display( {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: info => {
        const model = info.row.original;
        const modelUuid = model.uuid || model.name;

        const actionIcon = (
          <span className={ pageStyles.action_icon }>
            <Icon icon={ FiMoreVertical } size="md" />
          </span>
        );

        return (
          <div style={ { display: 'flex', justifyContent: 'center', width: '100%' } }>
            <Menu icon={ actionIcon } variant='menu'>
              <MenuItem
                value="Edit model"
                onClick={ () => handleEditModel( modelUuid ) }
                leadingIcon={ <Icon icon={ FiEdit2 } size="sm" /> }
              />
              <MenuItem
                value="Delete model"
                onClick={ () => handleDeleteModel( modelUuid, model.publicName || model.name ) }
                leadingIcon={ <Icon icon={ FiTrash2 } size="sm" /> }
              />
            </Menu>
          </div>
        );
      },
      size: 50
    } )
  ], [ useLocalZone, handleEditModel, handleDeleteModel ] );

  useEffect( () => {
    if( isError && error ) {
      console.error( 'Error loading models:', error );
      toast.error( error.message || 'Failed to load models' );
    }
  }, [ isError, error ] );

  // The model detail screen unmounts its own <ToastContainer /> during the
  // route change, so a toast fired there cannot survive. Callers pass the
  // message via location state and the toast is raised here on mount; state
  // is then cleared to avoid re-firing on re-render or browser back.
  useEffect( () => {
    const state = location.state as { deletedToast?: string } | null;
    if ( state?.deletedToast ) {
      toast.success( state.deletedToast );
      navigate( location.pathname, { replace: true, state: null } );
    }
  }, [ location.state, location.pathname, navigate ] );

  // Sort models alphabetically by name
  const sortedModels = useMemo( () => {
    if ( !models ) return [];

    const sortByName = ( a: Model, b: Model ) => {
      const nameA = a.publicName || a.name || '';
      const nameB = b.publicName || b.name || '';
      return nameA.localeCompare( nameB );
    };

    return [ ...models ].sort( sortByName );
  }, [ models ] );

  const table = useReactTable<Model>( {
    data: sortedModels,
    columns,
    state: useMemo( () => ( {
      sorting,
      columnFilters,
      pagination
    } ), [ sorting, columnFilters, pagination ] ),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  } );

  const handleFilterChange = useCallback( ( e: React.ChangeEvent<HTMLInputElement> ) => {
    table.getColumn( 'publicName' )?.setFilterValue( e.target.value.toLowerCase() );
  }, [ table ] );

  return (
    <div className='page'>
      <ToastContainer />
      <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) } style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
        <span className='info_name'>
          <Heading as="h2">Models</Heading>
        </span>
        <Button
          variant="primary"
          size="md"
          icon={ <Icon icon={ FiPlus } /> }
          onClick={ handleAddModel }
        >
          Add Model
        </Button>
      </div>
      {
        !isLoading && (
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <Input
              id='model-filter'
              type='text'
              label='Filter by name'
              placeholder='Filter by name...'
              value={ ( table.getColumn( 'publicName' )?.getFilterValue() as string ) ?? '' }
              onChange={ handleFilterChange }
            />
          </div>
        )
      }
      {
        isLoading
          ? <Loader/>
          : <NativeTable table={ table } onRowClick={ handleRowClick } />
      }

      <ModelModal
        mode={ modalMode }
        isOpen={ isModalOpen }
        onClose={ handleCloseModal }
        onSuccess={ handleModalSuccess }
        modelId={ editingModelId }
        providers={ providers }
        isLoadingProviders={ isLoadingProviders }
      />

      { isDeleteModalOpen && deletingModel && (
        <DeleteModal
          isOpen={ true }
          onClose={ handleCloseDeleteModal }
          onDelete={ handleConfirmDelete }
          entityName={ deletingModel.name }
          entityType="model"
          body={ <Text as="p" size="md">This model may be used by Low-Code Agents, Full-Code Agents, or evaluations. Deleting it could cause those resources to fail.<br /><br />Please verify and update any affected configurations before proceeding.</Text> }
        />
      ) }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <ModelsPage />
    </Suspense>
  );
}